import React from 'react';

interface InputProps extends React.InputHTMLAttributes<HTMLInputElement> {
  label?: string;
  error?: string;
  hint?: string;
}

interface TextareaProps extends React.TextareaHTMLAttributes<HTMLTextAreaElement> {
  label?: string;
  error?: string;
  hint?: string;
}

interface SelectProps extends React.SelectHTMLAttributes<HTMLSelectElement> {
  label?: string;
  error?: string;
  hint?: string;
  options: { value: string; label: string }[];
  placeholder?: string;
}

const fieldStyles: React.CSSProperties = {
  display: 'flex',
  flexDirection: 'column',
  gap: '0.25rem',
};

const labelStyles: React.CSSProperties = {
  fontSize: 'var(--text-sm)',
  fontWeight: 500,
  color: 'var(--color-text-muted)',
};

const inputBaseStyles: React.CSSProperties = {
  background: 'var(--glass-bg-input)',
  backdropFilter: 'blur(12px) saturate(140%)',
  WebkitBackdropFilter: 'blur(12px) saturate(140%)',
  border: '1px solid rgba(255, 255, 255, 0.10)',
  borderRadius: 'var(--radius-interactive)',
  color: 'var(--color-text)',
  fontSize: 'var(--text-sm)',
  padding: '0.5625rem 0.875rem',
  width: '100%',
  transition: 'border-color var(--spring-duration) var(--spring-easing), box-shadow var(--spring-duration) var(--spring-easing)',
  outline: 'none',
};

const errorStyles: React.CSSProperties = {
  fontSize: 'var(--text-xs)',
  color: 'var(--color-error)',
};

const hintStyles: React.CSSProperties = {
  fontSize: 'var(--text-xs)',
  color: 'var(--color-text-faint)',
};

export const Input = React.forwardRef<HTMLInputElement, InputProps>(
  ({ label, error, hint, style, ...props }, ref) => (
    <div style={fieldStyles}>
      {label && <label style={labelStyles}>{label}</label>}
      <input
        ref={ref}
        style={{
          ...inputBaseStyles,
          ...(error ? { borderColor: 'var(--color-error)' } : {}),
          ...style,
        }}
        onFocus={e => { e.target.style.borderColor = error ? 'var(--color-error)' : 'var(--color-primary)'; e.target.style.boxShadow = error ? 'none' : '0 0 0 3px rgba(79,152,163,0.15)'; }}
        onBlur={e => { e.target.style.borderColor = error ? 'var(--color-error)' : 'rgba(255, 255, 255, 0.10)'; e.target.style.boxShadow = 'none'; }}
        {...props}
      />
      {error && <span style={errorStyles}>{error}</span>}
      {hint && !error && <span style={hintStyles}>{hint}</span>}
    </div>
  )
);
Input.displayName = 'Input';

export const Textarea = React.forwardRef<HTMLTextAreaElement, TextareaProps>(
  ({ label, error, hint, style, ...props }, ref) => (
    <div style={fieldStyles}>
      {label && <label style={labelStyles}>{label}</label>}
      <textarea
        ref={ref}
        style={{
          ...inputBaseStyles,
          minHeight: '100px',
          resize: 'vertical',
          ...(error ? { borderColor: 'var(--color-error)' } : {}),
          ...style,
        }}
        onFocus={e => { e.target.style.borderColor = error ? 'var(--color-error)' : 'var(--color-primary)'; e.target.style.boxShadow = error ? 'none' : '0 0 0 3px rgba(79,152,163,0.15)'; }}
        onBlur={e => { e.target.style.borderColor = error ? 'var(--color-error)' : 'rgba(255, 255, 255, 0.10)'; e.target.style.boxShadow = 'none'; }}
        {...props}
      />
      {error && <span style={errorStyles}>{error}</span>}
      {hint && !error && <span style={hintStyles}>{hint}</span>}
    </div>
  )
);
Textarea.displayName = 'Textarea';

export const Select = React.forwardRef<HTMLSelectElement, SelectProps>(
  ({ label, error, hint, options, placeholder, style, ...props }, ref) => (
    <div style={fieldStyles}>
      {label && <label style={labelStyles}>{label}</label>}
      <select
        ref={ref}
        style={{
          ...inputBaseStyles,
          cursor: 'pointer',
          ...(error ? { borderColor: 'var(--color-error)' } : {}),
          ...style,
        }}
        onFocus={e => { e.target.style.borderColor = error ? 'var(--color-error)' : 'var(--color-primary)'; }}
        onBlur={e => { e.target.style.borderColor = error ? 'var(--color-error)' : 'rgba(255,255,255,0.10)'; }}
        {...props}
      >
        {placeholder && <option value="">{placeholder}</option>}
        {options.map(opt => (
          <option key={opt.value} value={opt.value}>{opt.label}</option>
        ))}
      </select>
      {error && <span style={errorStyles}>{error}</span>}
      {hint && !error && <span style={hintStyles}>{hint}</span>}
    </div>
  )
);
Select.displayName = 'Select';
